import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useRouteMatch } from "react-router-dom";
import { makeStyles, Paper, Grid, Theme } from "@material-ui/core";
import { KButton } from "@kyburz/kdesign-system";
import BaseLayout from "../../components/layouts/BaseLayout";
import useSetState from "../../hooks/useSetState";
import GroupViewVehicles from "./GroupViewVehicles";
import GroupViewUsers from "./GroupViewUsers";
import {
  createVehicleGroup,
  getVehicleGroup,
  updateVehicleGroup,
} from "../../apiCalls/admin/vehicleGroups";
import { RootStateInterface } from "../../interfaces/ReduxInterface";
import { setAlert } from "../../redux/actions/actions";

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: 32,
    boxShadow: "none",
    overflow: "scroll",
  },
  buttons: {
    width: "100%",
    height: 72,
    display: "flex",
    justifyContent: "flex-end",
    position: "fixed",
    padding: "16px 48px 0 0",
    bottom: 0,
    right: 0,
    borderTop: `1px solid ${theme.palette.grey[300]}`,
    background: "white",
  },
  save: {
    marginLeft: 16,
  },
}));

const VehicleGroupView: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const goBack = () => history.push("/admin/fleet/groups");

  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );

  const match = useRouteMatch({
    path: "/admin/fleet/groups/:groupId/:activeTabURL",
    strict: true,
    sensitive: true,
  });
  const { activeTabURL } = match?.params as any;
  const { groupId } = match?.params as any;
  const [
    {
      activeTab,
      groupData,
      groupName,
      vehicleIds,
      userIds,
      canReserveIds,
      loading,
    },
    setState,
  ] = useSetState({
    activeTab: "vehicles",
    groupData: {},
    groupName: "",
    vehicleIds: [],
    userIds: [],
    canReserveIds: [],
    loading: false,
  });

  const tabs: { label: string; value: string }[] = [
    { label: "Vehicles", value: "vehicles" },
    { label: "Users", value: "users" },
  ];

  const handleTabClick = (event, newValue) => {
    history.push(`/admin/fleet/groups/${groupId}/${newValue}`);
    setState({ activeTab: newValue });
  };

  const handleNameChange = (value: string): void => {
    setState({ groupName: value });
  };

  const handleCheckedIds = (name: string, ids: string[]): void => {
    setState({ [name]: ids });
  };

  const handleSave = (): void => {
    setState({ loading: true });
    if (groupId === "create") {
      createVehicleGroup(
        currentCustomer?.shortname,
        groupName,
        userIds
          .filter((x) => x)
          .map((x) => ({
            user_id: x,
            edit_vehicle: true,
            create_reservation: canReserveIds.includes(x),
          })),
        vehicleIds.filter((x) => x),
      ).then((res) => {
        if (res.data) {
          setState({ loading: false });
          goBack();
          dispatch(setAlert("Created Group successfully", "success"));
        } else {
          const errorMessage =
            JSON.stringify(
              res?.detail?.errors?.reduce((a, b) => Object.assign(a, b)),
            ) || "Error creating Group";
          setState({ loading: false });
          dispatch(setAlert(errorMessage, "error"));
        }
      });
    } else {
      updateVehicleGroup(
        currentCustomer?.shortname,
        groupData?._id,
        groupName,
        userIds
          .filter((x) => x)
          .map((x) => ({
            user_id: x,
            edit_vehicle: true,
            create_reservation: canReserveIds.includes(x),
          })),
        vehicleIds.filter((x) => x),
      ).then((res) => {
        if (res.data) {
          setState({ loading: false });
          goBack();
          dispatch(setAlert("Updated successfully", "success"));
        } else {
          const errorMessage =
            JSON.stringify(
              res?.detail?.errors?.reduce((a, b) => Object.assign(a, b)),
            ) || "Error creating Group";
          setState({ loading: false });
          dispatch(setAlert(errorMessage, "error"));
        }
      });
    }
  };

  useEffect(() => {
    if (groupId && groupId !== "create") {
      getVehicleGroup(currentCustomer?.shortname, groupId).then((res) => {
        const { data } = res;
        setState({ groupData: data });
      });
    }
  }, [groupId]);

  useEffect(() => {
    setState({ activeTab: activeTabURL });
  }, []);

  useEffect(() => {
    setState({
      groupName: groupData?.name || "",
      vehicleIds: groupData?.vehicles || [],
      userIds: groupData?.users?.map((x) => x?.user?.user_id) || [],
    });
  }, [groupData]);

  return (
    <BaseLayout
      title={groupId === "create" ? "Create Group" : "Edit Group"}
      handleArrowClick={goBack}
      tabs={tabs}
      handleTabChange={handleTabClick}
      activeTab={activeTab}
    >
      <div style={{ paddingTop: 20 }}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Paper className={classes.paper}>
              <div
                style={{ display: activeTab === "vehicles" ? "block" : "none" }}
              >
                <GroupViewVehicles
                  groupData={groupData}
                  groupName={groupName}
                  handleNameChange={handleNameChange}
                  handleCheckedIds={handleCheckedIds}
                  vehicleIdsLength={
                    vehicleIds?.filter((x) => x !== undefined).length
                  }
                />
              </div>
              <div
                style={{ display: activeTab === "users" ? "block" : "none" }}
              >
                <GroupViewUsers
                  groupData={groupData}
                  groupName={groupName}
                  handleNameChange={handleNameChange}
                  handleCheckedIds={handleCheckedIds}
                  userIdsLength={userIds?.filter((x) => x !== undefined).length}
                />
              </div>
            </Paper>
          </Grid>
        </Grid>
      </div>
      <div className={classes.buttons}>
        <KButton children="Cancel" theme="secondary" onClick={goBack} />
        <div className={classes.save}>
          <KButton
            children="Save and close"
            onClick={handleSave}
            loading={loading}
            disabled={loading}
          />
        </div>
      </div>
    </BaseLayout>
  );
};

export default VehicleGroupView;
